import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import {
  UnifiedCampaign,
  CampaignWizardData,
  CampaignStatus,
  CampaignType,
  VoiceProvider,
  PhoneProvider,
} from '@/types/unifiedCampaign';
import { useAuth } from './useAuth';
import { useOptimizedSupabaseScripts } from './useOptimizedSupabaseScripts';
import { useToast } from '@/hooks/use-toast';

export function useUnifiedCampaigns(enabled: boolean = true) {
  // console.log('useUnifiedCampaign:' + enabled);
  const [campaigns, setCampaigns] = useState<UnifiedCampaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();
  const { scripts: cachedScripts } = useOptimizedSupabaseScripts();
  const { toast } = useToast();

  // Fetch campaigns from Supabase
  const fetchCampaigns = async () => {
    if (!user) return;

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('campaigns')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      setCampaigns(
        (data || []).map((campaign) => ({
          ...campaign,
          campaign_type: campaign.campaign_type as CampaignType,
          status: campaign.status as CampaignStatus,
          phone_provider: campaign.phone_provider as PhoneProvider,
          voice_provider: campaign.voice_provider as VoiceProvider,
          ai_model: campaign.ai_model as 'grok' | 'openai',
          created_at: new Date(campaign.created_at),
          updated_at: new Date(campaign.updated_at),
          started_at: campaign.started_at
            ? new Date(campaign.started_at)
            : undefined,
          completed_at: campaign.completed_at
            ? new Date(campaign.completed_at)
            : undefined,
          activated_at: campaign.activated_at
            ? new Date(campaign.activated_at)
            : undefined,
          deactivated_at: campaign.deactivated_at
            ? new Date(campaign.deactivated_at)
            : undefined,
          scheduled_start: campaign.scheduled_start
            ? new Date(campaign.scheduled_start)
            : undefined,
          tags: campaign.tags || [],
          stats:
            typeof campaign.stats === 'string'
              ? JSON.parse(campaign.stats)
              : campaign.stats,
          transfer_settings:
            typeof campaign.transfer_settings === 'string'
              ? JSON.parse(campaign.transfer_settings)
              : campaign.transfer_settings,
          voicemail_settings:
            typeof campaign.voicemail_settings === 'string'
              ? JSON.parse(campaign.voicemail_settings)
              : campaign.voicemail_settings,
          ai_settings:
            typeof campaign.ai_settings === 'string'
              ? JSON.parse(campaign.ai_settings)
              : campaign.ai_settings,
        }))
      );
    } catch (err) {
      console.error('Error fetching campaigns:', err);
      setError(
        err instanceof Error ? err.message : 'Failed to fetch campaigns'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCampaigns();
  }, [user]);

  const createCampaign = async (
    wizardData: CampaignWizardData
  ): Promise<UnifiedCampaign> => {
    if (!user) throw new Error('User not authenticated');

    try {
      // Check if we need to add the warm intro mode tag using cached script data
      let tags = wizardData.tags || [];

      // Get the script ID to check for safe mode (cached from script hook)
      const scriptId = wizardData.outbound_script_id || wizardData.script_id;
      if (scriptId && cachedScripts) {
        const script = cachedScripts.find((s) => s.id === scriptId);

        // If script uses low risk mode, add the warm intro tag
        if (script?.use_low_risk_mode && !tags.includes('🕊️ Warm Intro Mode')) {
          tags = [...tags, '🕊️ Warm Intro Mode'];
        }
      }

      // Compute the primary script_id based on campaign type
      let primaryScriptId = wizardData.script_id;
      if (wizardData.campaign_type === 'outbound_inbound') {
        primaryScriptId = wizardData.outbound_script_id;
      }

      // Destructure vapi_key_name to prevent it from being passed as top-level column
      const { vapi_key_name, ...cleanWizardData } = wizardData;

      const campaignData = {
        user_id: user.id,
        ...cleanWizardData,
        script_id: primaryScriptId, // Set the computed primary script_id
        client_id: wizardData.client_id || null, // Include client_id
        tags,
        status: 'draft' as CampaignStatus,
        ai_settings: wizardData.ai_settings || {},
        transfer_settings: wizardData.transfer_settings || {
          enabled: false,
          transferNumber: null,
          transferConditions: [],
        },
        voicemail_settings: wizardData.voicemail_settings || {
          enabled: false,
          customMessage: null,
        },
        max_call_duration: wizardData.max_call_duration || 30,
        // Let database trigger handle record_calls default for inbound campaigns
        ...(wizardData.record_calls !== undefined && {
          record_calls: wizardData.record_calls,
        }),
        scheduled_start: wizardData.scheduled_start?.toISOString(),
      };

      const { data, error } = await supabase
        .from('campaigns')
        .insert([campaignData])
        .select()
        .single();

      if (error) throw error;
      //If outbound campaign, create queue items for calls
      let supabase_args = {
        body: {
          campaign_id: data.id,
        },
      };

      if (campaignData.campaign_type === 'outbound') {
        const { data: createQueueItemsResult, error: createQueueItemsError } =
          await supabase.functions.invoke(
            'queue_outgoing_campaign_calls',
            supabase_args
          );

        if (createQueueItemsError) {
          //Delete the already created campaign to avoid confusion
          await supabase.from('campaigns').delete().eq('id', data.id);
          throw createQueueItemsError;
        }
      }

      // Auto-create sister inbound campaign for outbound_inbound type
      if (wizardData.campaign_type === 'outbound_inbound') {
        const inboundData = {
          ...campaignData,
          name: `${wizardData.name} (Inbound)`,
          campaign_type: 'inbound',
          script_id: wizardData.inbound_script_id, // Use inbound script for sister campaign
          prospect_list_id: null, // Inbound doesn't need prospect list
        };

        await supabase.from('campaigns').insert([inboundData]);
      }

      await fetchCampaigns();

      // Transform the data response to match our interface
      const transformedData: UnifiedCampaign = {
        ...data,
        campaign_type: data.campaign_type as CampaignType,
        status: data.status as CampaignStatus,
        phone_provider: data.phone_provider as PhoneProvider,
        voice_provider: data.voice_provider as VoiceProvider,
        ai_model: data.ai_model as 'grok' | 'openai',
        created_at: new Date(data.created_at),
        updated_at: new Date(data.updated_at),
        started_at: data.started_at ? new Date(data.started_at) : undefined,
        completed_at: data.completed_at
          ? new Date(data.completed_at)
          : undefined,
        activated_at: data.activated_at
          ? new Date(data.activated_at)
          : undefined,
        deactivated_at: data.deactivated_at
          ? new Date(data.deactivated_at)
          : undefined,
        scheduled_start: data.scheduled_start
          ? new Date(data.scheduled_start)
          : undefined,
        tags: data.tags || [],
        stats:
          typeof data.stats === 'string' ? JSON.parse(data.stats) : data.stats,
        transfer_settings:
          typeof data.transfer_settings === 'string'
            ? JSON.parse(data.transfer_settings)
            : data.transfer_settings,
        voicemail_settings:
          typeof data.voicemail_settings === 'string'
            ? JSON.parse(data.voicemail_settings)
            : data.voicemail_settings,
        ai_settings:
          typeof data.ai_settings === 'string'
            ? JSON.parse(data.ai_settings)
            : data.ai_settings,
      };

      return transformedData;
    } catch (err) {
      console.error('Error creating campaign:', err);
      throw err;
    }
  };

  const updateCampaign = async (
    id: string,
    updates: Partial<UnifiedCampaign>
  ) => {
    try {
      // Transform frontend types to database types
      const dbUpdates: any = {};

      Object.keys(updates).forEach((key) => {
        const value = updates[key as keyof UnifiedCampaign];
        if (value instanceof Date) {
          dbUpdates[key] = value.toISOString();
        } else if (typeof value === 'object' && value !== null) {
          dbUpdates[key] = JSON.stringify(value);
        } else {
          dbUpdates[key] = value;
        }
      });

      dbUpdates.updated_at = new Date().toISOString();

      const { error } = await supabase
        .from('campaigns')
        .update(dbUpdates)
        .eq('id', id);

      if (error) throw error;

      await fetchCampaigns();
    } catch (err) {
      console.error('Error updating campaign:', err);
      throw err;
    }
  };

  const updateCampaignStatus = async (id: string, status: CampaignStatus) => {
    const targetCampaign = campaigns.find((c) => c.id === id);
    if (!targetCampaign) {
      console.error('Campaign not found:', id);
      return;
    }

    // Validate inbound campaign activation
    if (status === 'active' && targetCampaign.campaign_type === 'inbound') {
      const { validateInboundCampaignActivation } = await import(
        '@/utils/campaignValidation'
      );
      const validation = validateInboundCampaignActivation(
        targetCampaign,
        campaigns
      );

      if (!validation.isValid) {
        throw new Error(validation.error);
      }
    }

    const updates: Partial<UnifiedCampaign> = { status };
    const now = new Date();

    if (status === 'active') {
      updates.started_at = now;
      if (campaigns.find((c) => c.id === id)?.campaign_type === 'inbound') {
        updates.activated_at = now;
      }
    } else if (status === 'completed' || status === 'cancelled') {
      updates.completed_at = now;
    } else if (status === 'inactive') {
      updates.deactivated_at = now;
    }

    // Clear vapi_assistant_id when pausing to prevent cross-contamination
    if (status === 'paused') {
      updates.vapi_assistant_id = null;
    }

    await updateCampaign(id, updates);

    // Handle campaign execution based on type
    const campaign = campaigns.find((c) => c.id === id);

    if (campaign && status === 'active') {
      // For outbound_inbound: provision inbound first, then attempt outbound
      if (campaign.campaign_type === 'outbound_inbound') {
        try {
          await configureInboundWebhook(campaign);
        } catch (error) {
          console.error('Inbound webhook configuration failed:', error);
          // Don't prevent campaign from starting
        }

        // Attempt outbound execution but don't fail if it errors
        try {
          await startCampaignExecution(campaign);
        } catch (error) {
          console.error('Outbound execution failed:', error);
          toast({
            title: 'Partial Campaign Start',
            description: 'Campaign started but outbound execution failed',
            variant: 'destructive',
          });
        }
      }
      // For inbound only: just configure webhook (auto-rebuild)
      else if (campaign.campaign_type === 'inbound') {
        try {
          await configureInboundWebhook(campaign);
        } catch (error) {
          console.error('Inbound webhook configuration failed:', error);
          // Don't prevent campaign from starting
        }
      }
      // For outbound only: just start execution
      else if (campaign.campaign_type === 'outbound') {
        await startCampaignExecution(campaign);
      }
    }

    // Handle pause/stop for outbound components
    if (
      campaign &&
      (campaign.campaign_type === 'outbound' ||
        campaign.campaign_type === 'outbound_inbound')
    ) {
      if (status === 'paused') {
        // await pauseCampaignExecution(id);
        await pauseOutgoingCampaignExecution(campaign);
      } else if (status === 'completed' || status === 'cancelled') {
        // await stopCampaignExecution(id);
        await stopOutgoingCampaignExecution(campaign);
      }
    }
  };

  const startOutgoingCampaignExecution = async (campaign: UnifiedCampaign) => {
    //Mark all queued calls as idle
    try {
      const { data: rows } = await supabase
        .from('outgoing_call_queue')
        .select('id,status,campaign_id,user_id');
      // .eq('campaign_id', campaign.id)
      // .in('status', ['paused']);
      // console.log('Queued calls for campaign:', rows);
      const { data: updatedRows, error: initQueueError } = await supabase
        .from('outgoing_call_queue')
        .update({ status: 'idle' })
        .eq('campaign_id', campaign.id)
        .in('status', ['paused'])
        .select();
      if (initQueueError) {
        toast({
          title: 'Campaign Start Failed',
          description: initQueueError.message,
          variant: 'destructive',
        });
      } else {
        // console.log('Updated queued calls to idle:', updatedRows);
        toast({
          title: 'Campaign Started',
          description: `Campaign Started Successfully `,
          variant: 'default',
        });
      }
    } catch (error) {
      console.error('Failed to start campaign execution:', error);
      throw error;
    }
  };
  const pauseOutgoingCampaignExecution = async (campaign: UnifiedCampaign) => {
    //Mark all queued calls as idle
    try {
      const { error: initQueueError } = await supabase
        .from('outgoing_call_queue')
        .update({ status: 'paused' })
        .eq('campaign_id', campaign.id)
        .in('status', ['idle']);
      if (initQueueError) {
        toast({
          title: 'Campaign Pause Failed',
          description: initQueueError.message,
          variant: 'destructive',
        });
      } else {
        toast({
          title: 'Campaign Paused',
          description: `Campaign Paused Successfully `,
          variant: 'default',
        });
      }
    } catch (error) {
      // console.error('Failed to pause campaign execution:', error);
      throw error;
    }
  };
  const stopOutgoingCampaignExecution = async (campaign: UnifiedCampaign) => {
    //Mark all queued calls as idle
    try {
      const { error: initQueueError } = await supabase
        .from('outgoing_call_queue')
        .update({ status: 'stopped' })
        .eq('campaign_id', campaign.id)
        .in('status', ['idle', 'paused']);
      if (initQueueError) {
        toast({
          title: 'Campaign Stop Failed',
          description: initQueueError.message,
          variant: 'destructive',
        });
      } else {
        toast({
          title: 'Campaign Stopped',
          description: `Campaign Stopped Successfully `,
          variant: 'default',
        });
      }
    } catch (error) {
      console.error('Failed to stop campaign execution:', error);
      throw error;
    }
  };
  const startCampaignExecution = async (campaign: UnifiedCampaign) => {
    try {
      // Get script and prospect list data from Supabase
      const scriptId = campaign.outbound_script_id || campaign.script_id;
      const prospectListId = campaign.prospect_list_id;

      if (!scriptId || !prospectListId) {
        throw new Error('Campaign missing script ID or prospect list ID');
      }

      // Fetch script from Supabase
      const { data: script, error: scriptError } = await supabase
        .from('scripts')
        .select('*')
        .eq('id', scriptId)
        .eq('user_id', user?.id)
        .single();

      if (scriptError || !script) {
        throw new Error(
          `Script not found: ${scriptError?.message || 'Unknown error'}`
        );
      }

      // Fetch prospect list from Supabase
      const { data: prospectList, error: prospectError } = await supabase
        .from('prospect_lists')
        .select('*')
        .eq('id', prospectListId)
        .eq('user_id', user?.id)
        .single();

      if (prospectError || !prospectList) {
        throw new Error(
          `Prospect list not found: ${
            prospectError?.message || 'Unknown error'
          }`
        );
      }

      // Get next prospect from the list
      const prospects = Array.isArray(prospectList.prospects)
        ? prospectList.prospects
        : [];
      if (prospects.length === 0) {
        throw new Error('No prospects available in the list');
      }

      // Get the first prospect (in a real implementation, you'd track which prospects have been called)
      const nextProspect = prospects[0] as any;

      if (!nextProspect?.phone) {
        throw new Error('No phone number available for prospect');
      }

      // Initiate VAPI call with proper mergeContext
      const { data, error } = await supabase.functions.invoke(
        'initiate-vapi-call',
        {
          body: {
            campaignId: campaign.id,
            prospectId: nextProspect.id || nextProspect.name || 'unknown',
            scriptId: script.id,
            phoneNumber: nextProspect.phone,
            mergeContext: {
              industry: nextProspect.industry || '',
            },
          },
        }
      );

      if (error) {
        console.error('Error initiating VAPI call:', error);
        toast({
          title: 'Campaign Start Failed',
          description: 'Failed to initiate VAPI call: ' + error.message,
          variant: 'destructive',
        });
        throw error;
      }

      // Check if the edge function returned an error in the response
      if (data && !data.ok) {
        const errorMsg = data.error || data.message || 'VAPI call failed';
        const traceInfo = data.traceId ? ` (Trace: ${data.traceId})` : '';
        console.error('VAPI call failed:', data.error || data.message, {
          traceId: data.traceId,
        });
        toast({
          title: 'Campaign Start Failed',
          description: errorMsg + traceInfo,
          variant: 'destructive',
        });
        throw new Error(errorMsg);
      }

      // console.log('VAPI call initiated:', data);
      toast({
        title: 'Campaign Started',
        description: `Call initiated successfully for ${nextProspect.name}`,
        variant: 'default',
      });
    } catch (error) {
      console.error('Failed to start campaign execution:', error);
      throw error;
    }
  };

  const pauseCampaignExecution = async (campaignId: string) => {
    try {
      const { error } = await supabase.functions.invoke('execute-campaign', {
        body: {
          campaignId,
          action: 'pause',
        },
      });

      if (error) throw error;
    } catch (error) {
      console.error('Failed to pause campaign execution:', error);
      toast({
        title: 'Failed to Pause Campaign',
        description: error instanceof Error ? error.message : 'An error occurred. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const stopCampaignExecution = async (campaignId: string) => {
    try {
      const { error } = await supabase.functions.invoke('execute-campaign', {
        body: {
          campaignId,
          action: 'stop',
        },
      });

      if (error) throw error;
    } catch (error) {
      console.error('Failed to stop campaign execution:', error);
      toast({
        title: 'Failed to Stop Campaign',
        description: error instanceof Error ? error.message : 'An error occurred. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const configureInboundWebhook = async (campaign: UnifiedCampaign) => {

    try {
      // Get phone number VAPI ID if available
      let phoneNumberId = null;
      if (campaign.phone_number) {
        const { data: phoneData } = await supabase
          .from('phone_numbers')
          .select('vapi_id')
          .eq('phone_number', campaign.phone_number)
          .single();
        phoneNumberId = phoneData?.vapi_id;
      }

      const { data, error } = await supabase.functions.invoke(
        'configure-inbound-webhook',
        {
          body: {
            campaignId: campaign.id,
            scriptId: campaign.inbound_script_id || campaign.script_id,
            phoneNumberId: phoneNumberId,
            phoneNumber: campaign.phone_number,
          },
        }
      );

      if (error) {
        console.error('Error configuring inbound webhook:', error);

        // Check if it's a network/function error vs validation error
        if (
          error.message.includes('FunctionsFetchError') ||
          error.message.includes('network')
        ) {
          toast({
            title: 'Function Unavailable',
            description:
              'Inbound assistant configuration service is not available. Please ensure all Supabase functions are deployed.',
            variant: 'destructive',
          });
        } else {
          toast({
            title: 'Provisioning Failed',
            description: `Failed to configure inbound assistant: ${error.message}`,
            variant: 'destructive',
          });
        }
        throw error;
      }

      if (data?.error) {
        console.error('Inbound webhook configuration error:', data.error);
        toast({
          title: 'Provisioning Failed',
          description: `Failed to configure inbound assistant: ${data.error}`,
          variant: 'destructive',
        });
        throw new Error(data.error);
      }

      // Check Vapi provisioning status from the new response format
      if (data?.vapiPatchStatus === 'failed') {
        console.warn('Vapi PATCH failed:', data.vapiPatchError);
        toast({
          title: 'Database Updated, Vapi Failed',
          description: `Script updated in database but failed to provision to Vapi: ${data.vapiPatchError}`,
          variant: 'destructive',
        });
        // Don't throw - database update succeeded
      } else if (data?.vapiPatchStatus === 'skipped') {
        console.warn('Vapi PATCH skipped:', data.vapiPatchError);
        toast({
          title: 'Partially Configured',
          description: `Script updated in database but Vapi not provisioned: ${
            data.vapiPatchError || 'No API key'
          }`,
        });
      } else if (data?.vapiPatchStatus === 'success') {
        const timestamp = new Date().toLocaleTimeString();
        toast({
          title: 'Assistant Fully Provisioned ✓',
          description: `Inbound assistant updated in database and live Vapi phone (${timestamp})`,
        });
      } else {
        // Legacy response or unknown status
        toast({
          title: 'Assistant Configured',
          description: 'Inbound assistant has been successfully configured',
        });
      }
    } catch (error) {
      // Re-throw to maintain error flow but with better error context
      console.error('configureInboundWebhook failed:', error);
      throw error;
    }
  };

  const provisionInboundAssistant = async (campaignId: string) => {
    try {
      const campaign = campaigns.find((c) => c.id === campaignId);
      if (!campaign) {
        throw new Error('Campaign not found');
      }

      // console.log(
      //   `Provisioning inbound assistant for campaign: ${campaign.name}`
      // );
      toast({
        title: 'Provisioning Assistant',
        description: 'Configuring inbound assistant with latest script...',
      });

      await configureInboundWebhook(campaign);

      // configureInboundWebhook now handles its own toast messages based on provision status
      // so we don't need to show a redundant success toast here

      return { success: true };
    } catch (error) {
      console.error('Failed to provision inbound assistant:', error);
      toast({
        title: 'Provisioning Failed',
        description: `Failed to provision inbound assistant: ${error.message}`,
        variant: 'destructive',
      });
      return { success: false, error };
    }
  };

  const cloneCampaign = async (id: string): Promise<UnifiedCampaign> => {
    const original = campaigns.find((c) => c.id === id);
    if (!original) throw new Error('Campaign not found');

    const wizardData: CampaignWizardData = {
      name: `Copy of ${original.name}`,
      description: original.description,
      campaign_type: original.campaign_type,
      client_id: original.client_id,
      script_id: original.script_id,
      outbound_script_id: original.outbound_script_id,
      inbound_script_id: original.inbound_script_id,
      prospect_list_id: original.prospect_list_id,
      phone_provider: original.phone_provider,
      phone_number: original.phone_number,
      max_retries: original.max_retries,
      call_interval: original.call_interval,
      working_hours_enabled: original.working_hours_enabled,
      working_hours_start: original.working_hours_start,
      working_hours_end: original.working_hours_end,
      working_hours_timezone: original.working_hours_timezone,
      voice_provider: original.voice_provider,
      voice_id: original.voice_id,
      ai_model: original.ai_model,
      ai_settings: original.ai_settings,
      max_call_duration: original.max_call_duration,
      record_calls: original.record_calls,
      transfer_settings: original.transfer_settings,
      voicemail_settings: original.voicemail_settings,
      after_hours_message: original.after_hours_message,
      tags: original.tags,
    };

    return createCampaign(wizardData);
  };

  const restartCampaign = async (id: string, mode: 'fresh' | 'resume') => {
    const campaign = campaigns.find((c) => c.id === id);
    if (!campaign) throw new Error('Campaign not found');

    const statusesToReset = mode === 'fresh' ? ['stopped', 'finished'] : ['stopped'];

    const { error: queueError } = await supabase
      .from('outgoing_call_queue')
      .update({ status: 'idle' })
      .eq('campaign_id', id)
      .in('status', statusesToReset);

    if (queueError) {
      toast({
        title: 'Restart Failed',
        description: queueError.message,
        variant: 'destructive',
      });
      throw queueError;
    }

    await updateCampaign(id, {
      status: 'active',
      started_at: new Date(),
      completed_at: undefined,
    });

    toast({
      title: 'Campaign Restarted',
      description: mode === 'fresh' ? 'Restarting from the beginning.' : 'Resuming from last contact.',
    });
  };

  const deleteCampaign = async (id: string) => {
    try {
      const { error } = await supabase.from('campaigns').delete().eq('id', id);

      if (error) throw error;

      await fetchCampaigns();
    } catch (err) {
      console.error('Error deleting campaign:', err);
      throw err;
    }
  };

  const getCampaignStats = () => {
    const total = campaigns.length;
    const active = campaigns.filter((c) => c.status === 'active').length;
    const completed = campaigns.filter((c) => c.status === 'completed').length;
    const draft = campaigns.filter((c) => c.status === 'draft').length;
    const paused = campaigns.filter((c) => c.status === 'paused').length;

    return { total, active, completed, draft, paused };
  };

  const getCampaignsByType = (type?: string) => {
    if (!type || type === 'all') return campaigns;
    return campaigns.filter((c) => c.campaign_type === type);
  };

  return {
    campaigns,
    loading,
    error,
    fetchCampaigns,
    createCampaign,
    updateCampaign,
    updateCampaignStatus,
    cloneCampaign,
    restartCampaign,
    deleteCampaign,
    getCampaignStats,
    getCampaignsByType,
    provisionInboundAssistant,
  };
}
